import { jsx } from 'react/jsx-runtime';
import { breakpoints } from 'nicollet-tokens';
import styled from 'styled-components';
import { WebUrl } from '@web/url';
import { truthy } from '@web/utilities/truthy';
import { CDN_IMAGE_CONSTANTS } from './constants.js';
export { CDN_IMAGE_CONSTANTS } from './constants.js';
import { Scene7ParamMap, SourceImageFormat } from './types.js';
export { Scene7ParamMap, SourceImageFormat } from './types.js';

const computeScale = (actualLength, scale) => scale ? Math.round(actualLength * (1 + 0.2 * scale)) : actualLength;

const hasAlphaChannel = fmt => fmt.includes('-alpha');

const isScene7Image = src => src.includes('target.scene7.com') || src.includes('scene7.targetimg1.com');

const parseScene7ImageURL = src => {
  if (src.startsWith('/target.scene7.com')) {
    // This is a bug, we should capture it to notify
    // site merchandising. Adaptive has this baked in with watchtower reporting but
    // Nicollet doesn't have watchtower dependency. TODO: Should each site track this?
    return new WebUrl(src.replace('/target.scene7.com', 'https://target.scene7.com'));
  }

  return new WebUrl(src);
};

const getOptimizedImageFormat = (fmt, hasWebpSupport) => {
  if (!fmt) return '';
  return !hasWebpSupport ? fmt : hasAlphaChannel(fmt) ? 'webp-alpha' : 'webp';
};

const optimizeImageFormat = (src, hasWebpSupport) => {
  if (!src) return;
  if (!isScene7Image(src)) return src; // Parse the URL and set the format

  const url = parseScene7ImageURL(src);
  const query = url.searchParams; // Disabling the ESLint rule here as `query.get(Scene7ParamMap.format)` is sometimes passed in as empty string
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing

  const fmt = query.get(Scene7ParamMap.format) || SourceImageFormat.pjpeg;
  query.set(Scene7ParamMap.format, !hasWebpSupport ? fmt : hasAlphaChannel(fmt) ? 'webp-alpha' : 'webp');
  return url.toString();
};

const getFullUrlForSrc = (imageUrl, imageFormat, additionalParams, quality, imageHeight, imageWidth, scale) => {
  if (imageUrl.includes(CDN_IMAGE_CONSTANTS.curalateImageBaseUrl)) {
    // if the baseurl has curalate in it
    const strippedImageUrl = imageUrl.replace(/=.*$/gi, '=');
    const compression = quality ? `0.${quality}` : undefined;
    const widthScaled = imageWidth ? computeScale(imageWidth, scale) : undefined;
    const heightScaled = imageHeight ? computeScale(imageHeight, scale) : undefined;
    let newUrl = new WebUrl(strippedImageUrl).href;

    if (widthScaled && heightScaled) {
      newUrl += `/sc/${widthScaled}x${heightScaled}`;
    } else if (widthScaled) {
      newUrl += `/w/${widthScaled}`;
    } else if (heightScaled) {
      newUrl += `/h/${heightScaled}`;
    }

    const buildUrl = new WebUrl(newUrl);

    if (compression) {
      buildUrl.searchParams.set('compression', compression);
    }

    if (additionalParams) {
      buildUrl.setQuery({ ...additionalParams
      }, false);
    }

    return buildUrl.href;
  }

  return new WebUrl(imageUrl).setQuery({
    qlt: quality,
    fmt: imageFormat,
    ...(imageHeight ? {
      hei: computeScale(imageHeight, scale)
    } : {}),
    ...(imageWidth ? {
      wid: computeScale(imageWidth, scale)
    } : {}),
    ...additionalParams
  }, false).href;
};

const getImageUrlForBreakpoint = options => {
  const {
    breakpoint,
    imageParams,
    imageSize,
    additionalParameters = {},
    hasWebpSupport = false
  } = options;
  const {
    imageUrl,
    imageOfType
  } = imageParams;
  const imageFormat = getOptimizedImageFormat(imageOfType, hasWebpSupport);
  let width;
  let height;

  if (Array.isArray(imageSize)) {
    width = imageSize[0];
    height = imageSize[1];
  } else {
    width = imageSize;
    height = imageSize;
  }

  return getFullUrlForSrc(imageUrl, imageFormat, additionalParameters, breakpoint === '0' ? 65 : 85, height, width);
};
const getImagesByBreakpoint = options => {
  const {
    resolutionsByBreakpoint,
    imageParams,
    additionalParameters,
    hasWebpSupport
  } = options;

  if (!resolutionsByBreakpoint || !imageParams) {
    return;
  }

  return Object.fromEntries(Object.entries(resolutionsByBreakpoint).map(([breakpoint, imageSize]) => {
    if (!imageSize) {
      return undefined;
    }

    const imageUrl = getImageUrlForBreakpoint({
      breakpoint: breakpoint,
      imageSize,
      imageParams,
      additionalParameters,
      hasWebpSupport
    });
    return [breakpoint, imageUrl];
  }) // eslint-disable-next-line unicorn/no-array-callback-reference
  .filter(truthy));
};
const getImages = options => {
  const {
    imageParams,
    additionalParameters = {},
    hasWebpSupport = false
  } = options;
  const {
    imageUrl,
    imageHeight,
    imageWidth,
    imageOfType
  } = imageParams;
  const imageFormat = getOptimizedImageFormat(imageOfType, hasWebpSupport);
  return [{
    media: breakpoints.xl,
    imageUrl: getFullUrlForSrc(imageUrl, imageFormat, additionalParameters, 85, imageHeight, imageWidth, 4)
  }, {
    media: breakpoints.lg,
    imageUrl: getFullUrlForSrc(imageUrl, imageFormat, additionalParameters, 85, imageHeight, imageWidth, 3)
  }, {
    media: breakpoints.md,
    imageUrl: getFullUrlForSrc(imageUrl, imageFormat, additionalParameters, 85, imageHeight, imageWidth, 2)
  }, {
    media: '0px',
    imageUrl: getFullUrlForSrc(imageUrl, imageFormat, additionalParameters, 65, imageHeight, imageWidth)
  }];
};
const buildSourceTags = (images, hasWebpSupport) => images.map(({
  media,
  imageUrl
}) => jsx("source", {
  media: `(min-width: ${media})`,
  srcSet: optimizeImageFormat(imageUrl !== null && imageUrl !== void 0 ? imageUrl : '', hasWebpSupport)
}, `${media}-${imageUrl}`));
const ScalableImage = styled.img`
  ${props => (props.fixedWidth || props.hasRatio && props.fixedHeight) && 'width: 100%;'}
  ${props => (props.fixedHeight || props.hasRatio && props.fixedWidth) && 'height: 100%;'}
`;
const getImageForBreakpoint = (breakpoint, imagesByBreakpoint) => {
  const imageUrl = imagesByBreakpoint[breakpoint];

  if (!imageUrl) {
    return;
  }

  return {
    media: breakpoint === '0' ? '0px' : breakpoints[breakpoint],
    imageUrl
  };
};
const buildImagesByBreakpoint = imagesByBreakpoint => {
  if (!imagesByBreakpoint) {
    return;
  } // We extract the different breakpoints manually since they must be rendered in descending order by screen size.


  return [getImageForBreakpoint('xl', imagesByBreakpoint), getImageForBreakpoint('lg', imagesByBreakpoint), getImageForBreakpoint('md', imagesByBreakpoint), getImageForBreakpoint('sm', imagesByBreakpoint), getImageForBreakpoint('xs', imagesByBreakpoint), getImageForBreakpoint('0', imagesByBreakpoint) // eslint-disable-next-line unicorn/no-array-callback-reference
  ].filter(truthy);
};

export { ScalableImage, buildImagesByBreakpoint, buildSourceTags, getImageForBreakpoint, getImageUrlForBreakpoint, getImages, getImagesByBreakpoint, optimizeImageFormat };
